import { CompanyContact as CompanyContactModel } from '@models/business/CompanyContact'
import {
  getCompanyContacts,
  selectCompanyContacts,
  setCompanyContactQuery,
} from '@store/business/CompanyContactReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { PageTitle } from '@supplement-bacon/react-toolkit/components'
import { extractSortDataFromSorterResult } from '@supplement-bacon/react-toolkit/utils'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import MetadataKey from '@utils/MetadataKey'
import {
  Button,
  Space,
  TableColumnsType,
  TablePaginationConfig,
  Typography,
} from 'antd'
import { SorterResult } from 'antd/es/table/interface'
import { useEffect } from 'react'

const { Text } = Typography

interface Props {
  companyId: number
}

const Table = ({ companyId }: Props) => {
  const dispatch = useAppDispatch()
  const { isOpen } = useSBRM()
  const companyContacts = useAppSelector(selectCompanyContacts())
  const { isLoading, query } = useAppSelector((state) => state.companyContact)

  const companyContactColumns: TableColumnsType<CompanyContactModel> = [
    {
      title: 'Name',
      render: (record: CompanyContactModel) => (
        <Text>
          {record.lastName} {record.firstName}
        </Text>
      ),
    },
    {
      title: 'Phone',
      render: (record: CompanyContactModel) => <Text>{record.phone}</Text>,
    },
    {
      title: 'Position',
      render: (record: CompanyContactModel) => <Text>{record.position}</Text>,
    },
    {
      align: 'right',
      render: (record: CompanyContactModel) => (
        <Space>
          <a href={`mailto:${record.email}`}>
            <Button color="default" variant="outlined">
              Contact
            </Button>
          </a>
          <SBRM.Actions
            actions={[SBRMAction.Update, SBRMAction.Delete]}
            entity={Entity.companyContact}
            entityId={record.id}
          />
        </Space>
      ),
    },
  ]

  const handleTableChange = (
    pagination: TablePaginationConfig,
    sorter:
      | SorterResult<CompanyContactModel>
      | SorterResult<CompanyContactModel>[]
  ) => {
    const newQuery = {
      ...query,
      pagination,
      filters: {
        company: companyId ? [Number(companyId)] : [],
      },

      ...extractSortDataFromSorterResult(sorter),
    }
    dispatch(setCompanyContactQuery(newQuery))
    dispatch(getCompanyContacts(newQuery))
  }

  useEffect(() => {
    if (isOpen) {
      return
    }
    const newQuery: TableParams = {
      ...query,
      filters: {
        company: companyId ? [companyId] : [],
      },
    }

    dispatch(setCompanyContactQuery(newQuery))
    dispatch(getCompanyContacts(newQuery))
  }, [isOpen])

  return (
    <>
      <PageTitle
        level={5}
        title={'Company contacts'}
        toolbar={
          <SBRM.Actions
            actions={[SBRMAction.Create]}
            entity={Entity.companyContact}
            metadata={[
              {
                key: MetadataKey.CompanyId,
                value: companyId,
              },
            ]}
          />
        }
      />
      <SBTable
        columns={companyContactColumns}
        dataSource={companyContacts.map((contact) => ({
          ...contact,
          key: contact.id,
        }))}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={query.pagination}
      />
    </>
  )
}

type TableType = { Table: typeof Table }

export { Table, type TableType }
